// react
import React from 'react';
import ReactMarkdown from 'react-markdown';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import { Box, Typography } from '@mui/material';

// relative
import { ToolCallDisplay } from './ToolCallDisplay';

const AIAssistantAvatar = () => (
  <Box
    sx={{
      width: '32px',
      height: '32px',
      backgroundColor: '#212121',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '35%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
    }}
  >
    <svg
      width="26"
      height="26"
      viewBox="0 0 40 40"
      style={{ animation: 'aiSpin 4s linear infinite' }}
    >
      <defs>
        <linearGradient id="ai_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="hsl(63, 100%, 20%)" />
          <stop offset="100%" stopColor="hsl(63, 100%, 70%)" />
        </linearGradient>
      </defs>
      <circle
        cx="20"
        cy="20"
        r="12"
        stroke="url(#ai_gradient)"
        strokeWidth="2"
        fill="none"
      />
    </svg>

    <style>
      {`
        @keyframes aiSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
  </Box>
);

type AssistantMessagePart =
  | {
      type: 'text';
      content?: string;
    }
  | {
      type: 'thinking';
      content?: string;
    }
  | {
      type: 'tool-call';
      name?: string;
      input?: unknown;
      output?: unknown;
      state?: string;
      approval?: {
        id: string;
      };
    }
  | {
      type: string;
      content?: unknown;
      [key: string]: unknown;
    };

interface AssistantMessageProps {
  parts: AssistantMessagePart[]; // Using object to match TanStack AI's MessagePart type
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
}

export const AssistantMessage: React.FC<AssistantMessageProps> = ({
  parts,
  onApprove,
  onDeny,
}) => {
  const { t } = useTranslation('ai-assistant');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        mb: 2,
        px: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: '100%',
          p: 2,
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
          boxShadow: (theme) =>
            theme.palette.mode === 'light'
              ? '0 1px 2px rgba(0,0,0,0.05)'
              : '0 1px 2px rgba(0,0,0,0.3)',
          border: (theme) =>
            `1px solid ${
              theme.palette.mode === 'light'
                ? theme.palette.grey[200]
                : theme.palette.grey[700]
            }`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <AIAssistantAvatar />
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{ color: 'text.secondary' }}
          >
            {t('messages.assistant')}
          </Typography>
        </Box>

        {parts.map((part, idx) => {
          if (part.type === 'thinking') {
            const thinkingContent =
              typeof part.content === 'string'
                ? part.content
                : String(part.content || '');
            return (
              <Box
                key={idx}
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'info.lightest'
                      : 'rgba(33, 150, 243, 0.1)',
                  border: (theme) => `1px dashed ${theme.palette.info.light}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: '0.875rem' }}>💭</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    fontSize: '0.875rem',
                  }}
                >
                  {t('messages.thinking')}: {thinkingContent}
                </Typography>
              </Box>
            );
          }

          if (part.type === 'tool-call') {
            const getApproval = (
              approval: unknown,
            ): { id: string } | undefined => {
              if (
                approval &&
                typeof approval === 'object' &&
                'id' in approval &&
                typeof approval.id === 'string'
              ) {
                return { id: approval.id };
              }
              return undefined;
            };

            return (
              <ToolCallDisplay
                key={idx}
                toolName={String(part.name || t('tools.unknown'))}
                toolInput={part.input}
                toolOutput={part.output}
                state={part.state ? String(part.state) : undefined}
                approval={getApproval(part.approval)}
                onApprove={onApprove}
                onDeny={onDeny}
              />
            );
          }

          if (part.type === 'text') {
            const textContent =
              typeof part.content === 'string'
                ? part.content
                : String(part.content || '');
            // Check if this is a system notice
            const isSystemNotice = textContent.startsWith('[System Notice:');

            if (isSystemNotice) {
              return (
                <Box
                  key={idx}
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'warning.lightest'
                        : 'rgba(255, 152, 0, 0.15)',
                    border: (theme) =>
                      `1px solid ${theme.palette.warning.main}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      flexShrink: 0,
                    }}
                  >
                    <WarningRoundedIcon color="warning" />
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'warning.dark',
                      fontStyle: 'italic',
                    }}
                  >
                    {textContent
                      .replace('[System Notice:', '')
                      .replace(']', '')}
                  </Typography>
                </Box>
              );
            }

            return (
              <Box
                key={idx}
                sx={{
                  '& p': { margin: '0.5em 0' },
                  '& code': {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'light' ? 'grey.200' : 'grey.700',
                    p: 0.5,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875em',
                  },
                  '& pre': {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'light' ? 'grey.200' : 'grey.700',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    '& code': {
                      bgcolor: 'transparent',
                    },
                  },
                  '& ul, & ol': { pl: 2 },
                }}
              >
                <ReactMarkdown>{textContent}</ReactMarkdown>
              </Box>
            );
          }

          return null;
        })}
      </Box>
    </Box>
  );
};
